import { useState } from 'react';
import { formatDateTime, PATHS, useModal, useRouter } from '@belong/common';
import { IconCaretDown, IconCaretUp } from '@belong/icons';
import {
  Text,
  Button,
  Divider,
  Modal,
  Toast,
  Spinner,
  Image,
} from '@belong/ui';
import clsx from 'clsx';
import Label from 'components/Label/Label';
import {
  MaintenanceThread,
  updateThread,
} from 'pages/Accounts/Components/maintenance-agent/maintenance-agent.hooks';
import { STRINGS } from 'pages/Accounts/Components/maintenance-agent/maintenance-agent.strings';

export function MaintenanceAgentHistory({
  unitId,
  threads,
  refetchThreads,
}: {
  unitId: string;
  // Nullable to match the hook that supplies it, which returns `null` until the
  // history has loaded. The body already guards on `!threads`.
  threads: MaintenanceThread[] | null;
  refetchThreads: () => void;
}) {
  const router = useRouter();
  const [visibleCount, setVisibleCount] = useState(3);
  const [isCancelModalOpen, openCancelModal, closeCancelModal] = useModal();
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [toastText, setToastText] = useState<string | null>(null);
  const [closingThread, setClosingThread] = useState(false);

  const visibleThreads = threads?.slice(0, visibleCount);
  const hasMoreToShow = threads && threads.length > visibleCount;
  const canShowLess =
    visibleCount > 3 && visibleCount >= (threads?.length || 0);

  const handleShowMoreClick = () => {
    if (canShowLess) {
      setVisibleCount(3); // Show less - reset to 3
    } else {
      setVisibleCount((prev) => prev + 3); // Show 3 more
    }
  };

  const handleClickThread = (threadId: string) => {
    router.push(
      `${PATHS.RESIDENT_MAINTENANCE_SCHEDULE.replace(':unitId', unitId)}?threadId=${threadId}&from=list`,
    );
  };

  if (!threads || threads.length === 0) {
    return null;
  }

  return (
    <div>
      <div className="flex items-center gap-2sm">
        <Image
          src="/maintenance-agent/phin_logo.svg"
          alt="Phin"
          className="h-xl w-xl"
        />
        <Text variant="h3" fontWeight="semibold">
          {STRINGS.title}
        </Text>
      </div>
      <div className="mt-sm flex flex-col gap-2sm">
        {visibleThreads?.map((thread) => (
          <div key={thread.id} data-disabled={thread.ended} className="group">
            <div
              className={clsx(
                'rounded border border-dashed border-gray bg-light-gray p-sm transition-colors duration-hover hover:border-solid hover:border-navy group-data-[disabled=false]:cursor-pointer group-data-[disabled=true]:cursor-default group-data-[disabled=true]:border-solid group-data-[disabled=true]:border-gray',
              )}
              onClick={() => !thread.ended && handleClickThread(thread.id)}
            >
              <div className="flex flex-col justify-between gap-sm md:flex-row">
                <div className="flex min-w-0 flex-1 flex-col">
                  <Text
                    fontWeight="semibold"
                    className={clsx(
                      'truncate',
                      thread.ended && 'text-dark-gray',
                    )}
                  >
                    {thread.summary || thread.lastMessagePreview}
                  </Text>
                  <Text variant="p1" color="text-gray" className="shrink-0">
                    {formatDateTime({
                      dateTime: thread.createdAt,
                      format: 'LLLL do, Y',
                    })}
                  </Text>
                </div>
                <Label
                  text={thread.ended ? STRINGS.closed : STRINGS.chatInProgress}
                  color={thread.ended ? 'gray' : 'blue'}
                />
              </div>
              {!thread.ended && (
                <div className="mt-xs text-right">
                  <Button
                    variant="text"
                    size="small"
                    onClick={async (evt) => {
                      evt.stopPropagation();
                      setSelectedThreadId(thread.id);
                      openCancelModal();
                    }}
                  >
                    {STRINGS.closeIssue}
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {(hasMoreToShow || canShowLess) && (
        <div className="mt-sm text-right">
          <Button onClick={handleShowMoreClick} variant="text">
            <div className="flex">
              {canShowLess ? STRINGS.showLess : STRINGS.showMore}
              <div className="ml-xs flex items-center">
                {canShowLess ? (
                  <IconCaretUp height={15} width={15} />
                ) : (
                  <IconCaretDown height={15} width={15} />
                )}
              </div>
            </div>
          </Button>
        </div>
      )}
      <Divider
        className="mt-2xl"
        borderColor="border-gray"
        borderStyle="dashed"
      />
      <Modal
        isOpen={isCancelModalOpen}
        name="close_chat"
        onDismiss={closeCancelModal}
        size="small"
        overlayClassName="z-fullscreen"
        contentClassName="sm:w-[730px] md:w-[730px]"
      >
        {closingThread && <Spinner />}
        <div className="flex flex-col items-center px-2sm pt-3xl sm:px-lg sm:pt-2xl">
          <Text variant="h2" fontWeight="semibold" className="text-center">
            {STRINGS.areYouSure}
          </Text>
          <Text className="mt-xs text-center text-dark-gray">
            {STRINGS.cannotBeUndone}
          </Text>
          <Button
            variant="solid"
            buttonType="primary"
            className="mb-lg mt-2xl"
            onClick={async () => {
              setClosingThread(true);
              try {
                // This button only exists once a thread has been selected out
                // of a loaded list, so both reads were already unconditional.
                await updateThread(
                  selectedThreadId!,
                  threads!.find((thread) => thread.id === selectedThreadId)
                    ?.metadata!.assistant_id as string,
                );
                setTimeout(async () => {
                  await refetchThreads();
                  setToastText(STRINGS.conversationClosed);
                  closeCancelModal();
                  setClosingThread(false);
                }, 2000);
              } catch (error) {
                console.error(error);
                setToastText(STRINGS.errorClosingConversation);
                setClosingThread(false);
              }
            }}
          >
            {STRINGS.yesCloseIt}
          </Button>
          <Button
            variant="text"
            buttonType="danger"
            className="mb-xl"
            onClick={closeCancelModal}
          >
            {STRINGS.noDontCloseIt}
          </Button>
        </div>
      </Modal>
      <Toast
        variant={
          toastText === STRINGS.conversationClosed ? 'default' : 'danger'
        }
        isVisible={!!toastText}
        onClose={() => setToastText(null)}
        autocloseDuration={3000}
      >
        <div className="sm:max-w-[530px]">{toastText}</div>
      </Toast>
    </div>
  );
}
